import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Button from "../../atoms/Button";
import Select from "../../atoms/Select";
import SelectableUserList from "../../molecules/SelectableUserList";
import userService from "../../../services/userService";
import { resolveUserDisplayName } from "../../../utils/userIdentity";
import "./BandMembersModal.scss";

const ROLE_VALUES = ["OWNER", "ADMIN", "MODERATOR", "MEMBER", "GUEST"];
const STATUS_VALUES = ["ACTIVE", "INVITED", "SUSPENDED", "LEFT"];
const INVITE_STATUS_VALUES = ["PENDING", "ACCEPTED", "EXPIRED", "REVOKED"];

export default function BandMembersModal({
    isOpen,
    onClose,
    members,
    invites,
    currentUserId,
    onInviteMembers,
    onUpdateMember,
    onRemoveMember,
    onRevokeInvite,
    isSubmitting = false,
    isLoading = false,
    inviteError,
    canManage = false,
    allowRoleEdits = true,
    allowEmailInvites = true,
    friendCandidates = [],
    selectedFriendIds = [],
    onFriendSearch,
    onToggleFriend,
    onAddFriends,
    isFriendListLoading = false,
    isAddingFriends = false,
    friendListError,
}) {
    const { t } = useTranslation();
    const [inviteForm, setInviteForm] = useState({ emails: "", role: "MEMBER", expiresInHours: 168, friendSearch: "" });
    const roleOptions = useMemo(
        () => ROLE_VALUES.map((value) => ({ value, label: t(`bandsPage.members.roles.${value.toLowerCase()}`) })),
        [t]
    );
    const inviteRoleOptions = useMemo(() => roleOptions.filter((option) => option.value !== "OWNER"), [roleOptions]);
    const statusLabels = useMemo(
        () => Object.fromEntries(STATUS_VALUES.map((value) => [value, t(`bandsPage.members.status.${value.toLowerCase()}`)])),
        [t]
    );
    const inviteStatusLabels = useMemo(
        () => Object.fromEntries(INVITE_STATUS_VALUES.map((value) => [value, t(`bandsPage.members.inviteStatus.${value.toLowerCase()}`)])),
        [t]
    );

    const normalizedMembers = useMemo(() => members ?? [], [members]);
    const normalizedInvites = useMemo(() => invites ?? [], [invites]);

    useEffect(() => {
        if (isOpen) {
            setInviteForm((current) => ({ ...current, friendSearch: "" }));
        }
    }, [isOpen]);

    // Resolve and cache user names for member IDs to display friendly names
    const [userDirectory, setUserDirectory] = useState({});
    useEffect(() => {
        const ids = Array.from(new Set((members || []).map((m) => m.userId).filter(Boolean)));
        const missing = ids.filter((id) => userDirectory[id] === undefined);
        if (!missing.length) return;
        Promise.all(missing.map((id) => userService.fetchUserById(id).catch(() => null))).then((results) => {
            const entries = results
                .map((u) => (u ? [String(u.id), resolveUserDisplayName(u)] : null))
                .filter(Boolean);
            if (entries.length) {
                setUserDirectory((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [members]);

    const resolveUserName = (member) => {
        const directLabel = resolveUserDisplayName(member?.user);
        if (directLabel) {
            return directLabel;
        }
        const key = String(member?.userId);
        const value = userDirectory[key];
        if (value && typeof value === 'string' && value.trim().length > 0) return value;
        return t("bandsPage.members.userFallback", { id: member?.userId });
    };

    if (!isOpen) {
        return null;
    }

    const handleInviteChange = (field) => (event) => {
        const value = field === "expiresInHours" ? Number(event.target.value) : event.target.value;
        setInviteForm((current) => ({ ...current, [field]: value }));
    };

    const handleInviteSubmit = (event) => {
        event.preventDefault();
        if (!canManage) {
            return;
        }
        const emails = inviteForm.emails
            .split(/\s|,|;/)
            .map((value) => value.trim())
            .filter(Boolean);
        if (!emails.length) {
            return;
        }
        onInviteMembers({
            emails,
            role: inviteForm.role,
            expiresInHours: inviteForm.expiresInHours,
        });
        setInviteForm((current) => ({ ...current, emails: "" }));
    };

    return (
        <div className="band-members-modal">
            <div className="band-members-modal__backdrop" role="presentation" onClick={onClose} />
            <div className="band-members-modal__sheet" role="dialog" aria-modal="true" aria-labelledby="bandMembersTitle">
                <header className="band-members-modal__header">
                    <div>
                        <h2 id="bandMembersTitle">{t("bandsPage.members.title")}</h2>
                        <p>{t("bandsPage.members.subtitle")}</p>
                    </div>
                    <button type="button" className="band-members-modal__close" onClick={onClose} aria-label={t("bandsPage.common.closeButton")}>
                        ×
                    </button>
                </header>

                {/* Add friends at the top for quicker access */}
                <section className="band-members-modal__section">
                    <div className="band-members-modal__section-header">
                        <h3>{t("bandsPage.members.addFriends.title")}</h3>
                        <span>{friendCandidates.length}</span>
                    </div>
                    <div className={`band-members-modal__field ${!canManage ? 'ui-disabled' : ''}`}>
                        <span>{t("bandsPage.members.addFriends.searchLabel")}</span>
                        <input
                            type="search"
                            placeholder={t("bandsPage.members.addFriends.searchPlaceholder")}
                            value={inviteForm.friendSearch || ""}
                            onChange={(event) => {
                                const value = event.target.value;
                                setInviteForm((current) => ({ ...current, friendSearch: value }));
                                if (canManage) {
                                    onFriendSearch?.(value);
                                }
                            }}
                            disabled={isFriendListLoading || !canManage}
                        />
                    </div>
                    <SelectableUserList
                        items={friendCandidates.map((friend) => ({
                            id: friend.userId,
                            title: resolveUserDisplayName(friend, t("bandsPage.members.userFallback", { id: friend.userId })),
                            subtitle: friend.since
                                ? t("bandsPage.members.addFriends.friendSince", { date: new Date(friend.since).toLocaleDateString() })
                                : undefined,
                        }))}
                        selectedIds={selectedFriendIds}
                        onToggle={onToggleFriend}
                        isLoading={isFriendListLoading}
                        emptyMessage={t("bandsPage.members.addFriends.empty")}
                        disabled={!canManage}
                    />
                    {friendListError && <p className="band-members-modal__error" role="alert">{friendListError}</p>}
                    <div className="band-members-modal__actions band-members-modal__actions--end">
                        <Button
                            text={isAddingFriends ? t("bandsPage.members.addFriends.adding") : t("bandsPage.members.addFriends.action")}
                            variant="primary"
                            type="button"
                            disabled={isAddingFriends || !selectedFriendIds.length || !canManage}
                            onClick={onAddFriends}
                        />
                    </div>
                </section>

                <section className="band-members-modal__section">
                    <div className="band-members-modal__section-header">
                        <h3>{t("bandsPage.members.active.title")}</h3>
                        <span>{normalizedMembers.length}</span>
                    </div>
                    <ul className="band-members-modal__list">
                        {isLoading && (
                            <li className="band-members-modal__empty">{t("bandsPage.members.active.loading")}</li>
                        )}
                        {!isLoading && normalizedMembers.map((member) => {
                            const isSelf = member.userId === currentUserId;
                            const isOwner = member.role === "OWNER";
                            return (
                                <li key={member.membershipId} className="band-members-modal__list-item">
                                    <div className="band-members-modal__list-details">
                                        <div className="band-members-modal__identity">
                                            <span className="band-members-modal__identity-label">{resolveUserName(member)}</span>
                                            <span className={`band-members-modal__badge band-members-modal__badge--${member.status.toLowerCase()}`}>
                                                {statusLabels[member.status] || member.status}
                                            </span>
                                        </div>
                                        <span className="band-members-modal__meta">
                                            {member.joinedAt
                                                ? t("bandsPage.members.active.joined", { date: new Date(member.joinedAt).toLocaleDateString() })
                                                : t("bandsPage.members.active.joinedUnknown")}
                                        </span>
                                    </div>
                                    <div className="band-members-modal__list-actions">
                                        <Select
                                            value={member.role}
                                            onChange={(event) => onUpdateMember(member.membershipId, { role: event.target.value })}
                                            options={roleOptions}
                                            className="band-members-modal__role-select"
                                            disabled={isOwner || !canManage || !allowRoleEdits}
                                        />
                                        {canManage && !isOwner && !isSelf && (
                                            <Button
                                                text={t("bandsPage.members.actions.remove")}
                                                variant="danger"
                                                size="small"
                                                onClick={() => onRemoveMember(member.membershipId)}
                                            />
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                        {!isLoading && !normalizedMembers.length && (
                            <li className="band-members-modal__empty">{t("bandsPage.members.active.empty")}</li>
                        )}
                    </ul>
                </section>

                <section className="band-members-modal__section">
                    <div className="band-members-modal__section-header">
                        <h3>{t("bandsPage.members.invites.title")}</h3>
                        <span>{normalizedInvites.length}</span>
                    </div>
                    <ul className="band-members-modal__list">
                        {isLoading && (
                            <li className="band-members-modal__empty">{t("bandsPage.members.invites.loading")}</li>
                        )}
                        {!isLoading && normalizedInvites.map((invite) => (
                            <li key={invite.id} className="band-members-modal__list-item">
                                <div className="band-members-modal__list-details">
                                    <div className="band-members-modal__identity">
                                        <span className="band-members-modal__identity-label">
                                            {invite.displayName
                                                || invite.email
                                                || t("bandsPage.members.userFallback", { id: invite.targetUserId })}
                                        </span>
                                        <span className={`band-members-modal__badge band-members-modal__badge--${invite.status.toLowerCase()}`}>
                                            {inviteStatusLabels[invite.status] || invite.status}
                                        </span>
                                    </div>
                                    <span className="band-members-modal__meta">
                                        {invite.expiresAt
                                            ? t("bandsPage.members.invites.expires", { date: new Date(invite.expiresAt).toLocaleString() })
                                            : t("bandsPage.members.invites.expiresUnknown")}
                                    </span>
                                </div>
                                <div className="band-members-modal__list-actions">
                                    <span className="band-members-modal__invite-role">
                                        {roleOptions.find((option) => option.value === invite.role)?.label || invite.role}
                                    </span>
                                    {canManage && invite.status === "PENDING" && (
                                        <Button
                                            text={t("bandsPage.members.actions.revoke")}
                                            variant="secondary"
                                            size="small"
                                            onClick={() => onRevokeInvite(invite.id)}
                                        />
                                    )}
                                </div>
                            </li>
                        ))}
                        {!isLoading && !normalizedInvites.length && (
                            <li className="band-members-modal__empty">{t("bandsPage.members.invites.empty")}</li>
                        )}
                    </ul>
                </section>

                <section className="band-members-modal__section">
                    <div className="band-members-modal__section-header">
                        <h3>{t("bandsPage.members.invite.title")}</h3>
                    </div>
                    {allowEmailInvites ? (
                        <form className="band-members-modal__invite-form" onSubmit={handleInviteSubmit}>
                            <label className="band-members-modal__field">
                                <span>{t("bandsPage.members.invite.fields.emails")}</span>
                                <textarea
                                    value={inviteForm.emails}
                                    onChange={handleInviteChange("emails")}
                                    placeholder={t("bandsPage.members.invite.fields.emailsPlaceholder")}
                                    rows={3}
                                    disabled={!canManage}
                                />
                            </label>
                            <div className="band-members-modal__grid">
                                <label className="band-members-modal__field">
                                    <span>{t("bandsPage.members.invite.fields.role")}</span>
                                    <Select
                                        value={inviteForm.role}
                                        onChange={handleInviteChange("role")}
                                        options={inviteRoleOptions}
                                        disabled={!canManage}
                                    />
                                </label>
                                <label className="band-members-modal__field">
                                    <span>{t("bandsPage.members.invite.fields.expiry")}</span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={720}
                                        value={inviteForm.expiresInHours}
                                        onChange={handleInviteChange("expiresInHours")}
                                        disabled={!canManage}
                                    />
                                </label>
                            </div>
                            {inviteError && <p className="band-members-modal__error" role="alert">{inviteError}</p>}
                            {!canManage && (
                                <p className="band-members-modal__hint">{t("bandsPage.members.invite.hint")}</p>
                            )}
                            <div className="band-members-modal__actions">
                                <Button text={t("bandsPage.members.actions.close")} variant="secondary" onClick={onClose} type="button" />
                                <Button
                                    text={isSubmitting ? t("bandsPage.members.invite.sending") : t("bandsPage.members.invite.send")}
                                    variant="primary"
                                    type="submit"
                                    disabled={isSubmitting || !canManage}
                                />
                            </div>
                        </form>
                    ) : (
                        <>
                            <p className="band-members-modal__hint">{t("bandsPage.members.invite.bandWorkspaceHint")}</p>
                            {inviteError && <p className="band-members-modal__error" role="alert">{inviteError}</p>}
                        </>
                    )}
                </section>

                
            </div>
        </div>
    );
}

BandMembersModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    members: PropTypes.arrayOf(PropTypes.shape({
        membershipId: PropTypes.string.isRequired,
        userId: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        joinedAt: PropTypes.string,
    })),
    invites: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        email: PropTypes.string,
        targetUserId: PropTypes.string,
        role: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        expiresAt: PropTypes.string,
    })),
    currentUserId: PropTypes.string,
    onInviteMembers: PropTypes.func.isRequired,
    onUpdateMember: PropTypes.func.isRequired,
    onRemoveMember: PropTypes.func.isRequired,
    onRevokeInvite: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    isLoading: PropTypes.bool,
    inviteError: PropTypes.string,
    canManage: PropTypes.bool,
    allowRoleEdits: PropTypes.bool,
    allowEmailInvites: PropTypes.bool,
    friendCandidates: PropTypes.arrayOf(PropTypes.shape({
        userId: PropTypes.string.isRequired,
        username: PropTypes.string,
        since: PropTypes.string,
    })),
    selectedFriendIds: PropTypes.arrayOf(PropTypes.string),
    onFriendSearch: PropTypes.func,
    onToggleFriend: PropTypes.func,
    onAddFriends: PropTypes.func,
    isFriendListLoading: PropTypes.bool,
    isAddingFriends: PropTypes.bool,
    friendListError: PropTypes.string,
};
